/**
 * End state of a batch upload: exactly which files landed and which did not.
 *
 * This is the panel a user checks after uploading. It stays on screen until
 * dismissed, groups by outcome so nothing is buried, and links each successful
 * file to where it ended up in SharePoint.
 */

import { useMemo, useState } from "react";
import {
  ArrowSquareOut,
  CaretDown,
  CaretUp,
  CheckCircle,
  WarningCircle,
} from "@phosphor-icons/react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

export interface UploadResultEntry {
  /** Stable key; the file's index in the batch works fine. */
  id: string | number;
  fileName: string;
  success: boolean;
  /** Failure reason, shown verbatim so backend messages reach the user. */
  error?: string;
  /** Human-readable destination, e.g. "Gebouw 123 / EPC". */
  location?: string;
  /** Link to the stored file. */
  href?: string;
}

interface UploadResultsPanelProps {
  results: UploadResultEntry[];
  onDismiss: () => void;
  /** Rendered under the failures, e.g. a "probeer opnieuw" hint. */
  failureHint?: string;
}

export const UploadResultsPanel = ({
  results,
  onDismiss,
  failureHint,
}: UploadResultsPanelProps) => {
  const { succeeded, failed } = useMemo(
    () => ({
      succeeded: results.filter((result) => result.success),
      failed: results.filter((result) => !result.success),
    }),
    [results],
  );

  // Successes are the boring half: collapse them by default once there is
  // something that actually needs attention.
  const [showSucceeded, setShowSucceeded] = useState(true);

  if (results.length === 0) return null;

  const allSucceeded = failed.length === 0;

  return (
    <section
      className="border-border bg-card mb-6 rounded-lg border p-5 shadow-sm"
      aria-live="polite"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {allSucceeded ? (
            <CheckCircle
              size={24}
              weight="fill"
              className="text-moss-primary mt-0.5 shrink-0"
              aria-hidden
            />
          ) : (
            <WarningCircle
              size={24}
              weight="fill"
              className="text-destructive mt-0.5 shrink-0"
              aria-hidden
            />
          )}

          <div>
            <h2 className="text-foreground text-lg font-semibold">
              {allSucceeded
                ? "Upload voltooid"
                : succeeded.length > 0
                  ? "Upload deels gelukt"
                  : "Upload mislukt"}
            </h2>
            <p className="text-muted-foreground mt-0.5 text-sm">
              {succeeded.length} van {results.length} bestand
              {results.length === 1 ? "" : "en"} geüpload
              {failed.length > 0 && ` · ${failed.length} mislukt`}
            </p>
          </div>
        </div>

        <Button variant="outline" size="sm" onClick={onDismiss}>
          Sluiten
        </Button>
      </div>

      {/* Failures first: they are the only part that needs the user to act. */}
      {failed.length > 0 && (
        <div className="mt-5">
          <div className="mb-2 flex items-center gap-2">
            <h3 className="text-foreground text-sm font-semibold">Mislukt</h3>
            <Badge variant="destructive">{failed.length}</Badge>
          </div>

          <ul className="border-border divide-border divide-y rounded-md border">
            {failed.map((result) => (
              <li key={result.id} className="flex items-start gap-3 p-3">
                <WarningCircle
                  size={18}
                  weight="fill"
                  className="text-destructive mt-0.5 shrink-0"
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <p
                    className="text-foreground truncate text-sm font-medium"
                    title={result.fileName}
                  >
                    {result.fileName}
                  </p>
                  {result.error && (
                    <p className="text-destructive mt-1 text-xs break-words">
                      {result.error}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>

          {failureHint && (
            <p className="text-muted-foreground mt-2 text-xs">{failureHint}</p>
          )}
        </div>
      )}

      {succeeded.length > 0 && (
        <div className="mt-5">
          <div className="mb-2 flex items-center gap-2">
            <h3 className="text-foreground text-sm font-semibold">Geüpload</h3>
            <Badge variant="secondary">{succeeded.length}</Badge>

            {/* Only worth collapsing when the list is long enough to get in the way. */}
            {succeeded.length > 3 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSucceeded((previous) => !previous)}
                aria-expanded={showSucceeded}
              >
                {showSucceeded ? (
                  <CaretUp size={14} aria-hidden />
                ) : (
                  <CaretDown size={14} aria-hidden />
                )}
                {showSucceeded ? "Verbergen" : "Tonen"}
              </Button>
            )}
          </div>

          {showSucceeded && (
            <ul className="border-border divide-border divide-y rounded-md border">
              {succeeded.map((result) => (
                <li
                  key={result.id}
                  className="flex flex-wrap items-start gap-3 p-3"
                >
                  <CheckCircle
                    size={18}
                    weight="fill"
                    className="text-moss-primary mt-0.5 shrink-0"
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <p
                      className="text-foreground truncate text-sm font-medium"
                      title={result.fileName}
                    >
                      {result.fileName}
                    </p>
                    {result.location && (
                      <p className="text-muted-foreground mt-0.5 text-xs">
                        {result.location}
                      </p>
                    )}
                  </div>

                  {result.href && (
                    <a
                      href={result.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-foreground hover:text-moss-primary inline-flex shrink-0 items-center gap-1 text-xs font-medium underline"
                    >
                      Openen in SharePoint
                      <ArrowSquareOut size={14} aria-hidden />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Separator />

      <p className="text-muted-foreground text-xs">
        Geüploade bestanden zijn uit de lijst verwijderd. Mislukte bestanden
        blijven staan zodat je ze kunt aanpassen en opnieuw proberen.
      </p>
    </section>
  );
};

export default UploadResultsPanel;
